import { useAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import React, { createContext, ReactNode, useContext } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router'
import { StorageKeys } from '~/atoms/StorageKeys'
import { useUserState } from '~/atoms/UserState'
import { TCallAnalyticsTab } from '~/pages/Calls/CallDetails/CallDetailsAnalyticsContent'
import {
  buildCallTableColumnSorting,
  CallTableColumn,
  CallTableColumnSorting,
} from '~/pages/Calls/data/CallTableData'
import { useGetInfiniteCalls } from '~/queries/callQueries'
import { ApplicationRoutes } from '~/types/RoutingTypes'
import { Sentiment } from '~/types/SentimentTypes'
import {
  DateRange,
  FilterSection,
  SearchValue,
  SelectedFilterData,
  SortDirection,
} from '~/types/Shared'
import { stringifyQueryParams } from '~/utils/apiQueryUtils'
import {
  getBitValueOrDefault,
  getDateFromLongOrDefault,
  hasValue,
} from '~/utils/common'
import { areValuesEqual } from '~/utils/objectUtils'

import { useSharedState } from '@mangovoice/core'
import {
  Call,
  CallDirection,
  callFilterKeys,
  CallInstance,
  CallsQueryParams,
  CallStatus,
  CallType,
  PaginatedDataState,
  ResourceLoadingDisposition,
  stateParamToRouteParam,
} from '@mangovoice/types'

import { CallsRouteQueryParameters } from './CallsRoutingTypes'
import { tryGetQueryFilters } from './data/CallFilterData'

export interface CallsViewModelState {
  isCallsLoading?: boolean
  selectedCallId?: number
  selectedCallInitialTab?: TCallAnalyticsTab
  callsListState: PaginatedDataState<Call>
  callsListLoadingState?: ResourceLoadingDisposition
  filterData?: FilterSection[]
  totalFilteredCallCount?: number
  activeFilters?: SelectedFilterData[]
  dateTimeRange?: DateRange
  queryParameters?: CallsQueryParams
  ordering?: OrderingField<Call>[]
  columnSorting?: CallTableColumnSorting[]
  pbxUsersLoadingState?: ResourceLoadingDisposition
  selectedCallInstance?: CallInstance
  selectedCallInstanceLoadingState?: ResourceLoadingDisposition
  searchValue?: SearchValue
  audioTimestamp?: number
}

interface UseCallsViewModelParams {
  sharedStateKey: string
  deleteSharedOnUnmount?: boolean
}

enum DateRangeIdentifier {
  Today = 'today',
  Yesterday = 'yesterday',
  Last7Days = 'last_7_days',
  Last14Days = 'last_14_days',
  Last30Days = 'last_30_days',
}

const dateRangeParamToDateRangeIdentifier = (searchParams: URLSearchParams) => {
  const dateRangeParam = searchParams.get('date_range') as DateRangeIdentifier
  const aftr = searchParams.get('aftr')
  const bfr = searchParams.get('bfr')
  if (aftr && bfr) {
    return {
      after: new Date(Number(aftr)),
      before: new Date(Number(bfr)),
    }
  }
  let dateRange: DateRange | undefined
  if (!dateRangeParam) {
    return DateRange.last14Days()
  }

  switch (dateRangeParam) {
    case DateRangeIdentifier.Today:
      dateRange = DateRange.today()
      break
    case DateRangeIdentifier.Yesterday:
      dateRange = DateRange.yesterday()
      break
    case DateRangeIdentifier.Last7Days:
      dateRange = DateRange.last7Days()
      break
    case DateRangeIdentifier.Last14Days:
      dateRange = DateRange.last14Days()
      break
    case DateRangeIdentifier.Last30Days:
      dateRange = DateRange.last30Days()
      break
    default:
      dateRange = undefined
  }

  return dateRange
}

export const getCallsQueryParamsFromRouteQueryParams = (
  routeQueryParams: CallsRouteQueryParameters,
) => {
  const callsQueryParams: CallsQueryParams = {
    directions: routeQueryParams.dir as CallDirection[],
    duration_in_seconds_gte: routeQueryParams.dur_gte,
    duration_in_seconds_lt: routeQueryParams.dur_lt,
    is_missed:
      routeQueryParams.miss !== undefined
        ? routeQueryParams.miss == 1
          ? true
          : false
        : undefined,
    search: routeQueryParams.srch,
    statuses: routeQueryParams.status as CallStatus[],
    has_voicemail:
      routeQueryParams.vmr !== undefined
        ? Boolean(routeQueryParams.vmr)
        : undefined,
    is_externally_transferred:
      routeQueryParams.trnsfr !== undefined
        ? Boolean(routeQueryParams.trnsfr)
        : undefined,
    ordering: (routeQueryParams?.sb ?? '-started_at') as OrderingField<Call>[],
    types: ['standard'] as CallType[],
    related_user_ids: routeQueryParams.u,
    started_after: getDateFromLongOrDefault(routeQueryParams.aftr),
    started_before: getDateFromLongOrDefault(routeQueryParams.bfr),
    office_overall_sentiment_score:
      routeQueryParams.agent_sentiment as Sentiment[],
    customer_overall_sentiment_score:
      routeQueryParams.customer_sentiment as Sentiment[],
    voip_profile_ids: routeQueryParams.vp,
    t: routeQueryParams.t,
  }
  if (routeQueryParams.date_range) {
    const dateRangeIdentifier =
      routeQueryParams.date_range as DateRangeIdentifier
    let dateRange: DateRange | undefined

    switch (dateRangeIdentifier) {
      case DateRangeIdentifier.Today:
        dateRange = DateRange.today()
        break
      case DateRangeIdentifier.Yesterday:
        dateRange = DateRange.yesterday()
        break
      case DateRangeIdentifier.Last7Days:
        dateRange = DateRange.last7Days()
        break
      case DateRangeIdentifier.Last14Days:
        dateRange = DateRange.last14Days()
        break
      case DateRangeIdentifier.Last30Days:
        dateRange = DateRange.last30Days()
        break
      default:
        dateRange = undefined
    }

    if (dateRange) {
      callsQueryParams.started_after = dateRange.after
      callsQueryParams.started_before = dateRange.before
    }
  }

  return callsQueryParams
}
const callsPageSearchParamsAtom = atomWithStorage<string>(
  StorageKeys.CallsPageSearchParams,
  '',
)
const useCallsViewModel = (
  { sharedStateKey, deleteSharedOnUnmount }: UseCallsViewModelParams = {
    sharedStateKey: '__default',
    deleteSharedOnUnmount: false,
  },
) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { pbxId } = useUserState()
  const [searchParams, setSearchParams] = useSearchParams()
  const searchParamsString = searchParams.toString()
  const [callsPageSearchParams, setCallsPageSearchParams] = useAtom(
    callsPageSearchParamsAtom,
  )

  useEffect(() => {
    if (callsPageSearchParams && !searchParamsString) {
      setSearchParams(new URLSearchParams(callsPageSearchParams))
    }
  }, [])

  const dateTimeRange = useMemo(() => {
    // If there is no search params, use local storage value if set.
    // This prevents the date range from flickering when the page is loaded.
    if (!searchParamsString && callsPageSearchParams) {
      return dateRangeParamToDateRangeIdentifier(
        new URLSearchParams(callsPageSearchParams),
      )
    }
    return dateRangeParamToDateRangeIdentifier(
      new URLSearchParams(searchParamsString),
    )
  }, [searchParamsString, callsPageSearchParams])

  const getDefaultViewModelState = (): CallsViewModelState => {
    return {
      callsListState: {
        data: [],
        hasNextPage: false,
        nextPageOffset: 0,
      },
    }
  }

  const [__lifecycleState, __setState, sourceState, deleteSourceState] =
    useSharedState({
      key: sharedStateKey,
      namespace: 'calls',
      initialState: getDefaultViewModelState(),
    })

  const setState = (newState: CallsViewModelState) => {
    const ordering = searchParams.get('sb')?.split(',') ?? ['-started_at']
    const firstOrderingValue = ordering?.[0] ?? '-started_at'
    let columnSortingValue: CallTableColumnSorting
    switch (firstOrderingValue) {
      case '-started_at':
      case 'started_at':
        columnSortingValue = {
          column: CallTableColumn.DateTime,
          direction: firstOrderingValue.startsWith('-')
            ? SortDirection.Descending
            : SortDirection.Ascending,
        }
        break
      case '-duration_in_seconds':
      case 'duration_in_seconds':
        columnSortingValue = {
          column: CallTableColumn.Duration,
          direction: firstOrderingValue.startsWith('-')
            ? SortDirection.Descending
            : SortDirection.Ascending,
        }
        break
      default:
        columnSortingValue = {
          column: CallTableColumn.DateTime,
          direction: SortDirection.Ascending,
        }
    }

    const columnSorting = buildCallTableColumnSorting(
      columnSortingValue!.column,
      columnSortingValue!.direction!,
    )

    const newStateWithQueryParams = {
      ...newState,
      columnSorting,
    }

    __setState(newStateWithQueryParams)
  }

  const appendState = (newState: Partial<CallsViewModelState>) => {
    setState({ ...sourceState, ...newState })
  }

  useEffect(() => {
    return () => {
      if (deleteSharedOnUnmount) {
        deleteSourceState()
      }
    }
  }, [])
  const initialStateFromQueryParams = useMemo(() => {
    return getCallsQueryParamsFromRouteQueryParams(
      Object.fromEntries(searchParams),
    )
  }, [])
  const [stateFromQueryParams, setStateFromQueryParams] =
    useState<CallsQueryParams>(initialStateFromQueryParams)

  useEffect(() => {
    const currentQueryParams = Object.fromEntries(searchParams)
    const _stateFromQueryParams =
      getCallsQueryParamsFromRouteQueryParams(currentQueryParams)

    if (
      currentQueryParams &&
      stateFromQueryParams &&
      !areValuesEqual(stateFromQueryParams ?? {}, _stateFromQueryParams)
    ) {
      setStateFromQueryParams(_stateFromQueryParams)
      setCallsPageSearchParams(searchParamsString)
      appendState({}) // this is a bit of a hack to update the columnSorting when the search parameters change
    }
  }, [searchParamsString])

  // #region Queries
  const {
    isLoading: isCallsLoading,
    fetchNextPage,
    results,
    hasNextPage,
    totalCount,
    isFetchingNextPage,
  } = useGetInfiniteCalls(pbxId ?? 0, stateFromQueryParams)

  // #endregion

  //#region Actions
  const actions = {
    tryGetNextCallPage: () => {
      if (!hasNextPage) {
        return
      }
      fetchNextPage()
    },

    setSearchValue: (searchValue?: SearchValue) => {
      const existingParams = Object.fromEntries(searchParams)
      delete existingParams.srch
      if (searchValue?.value) {
        setSearchParams(
          stringifyQueryParams({ ...existingParams, srch: searchValue.value }),
        )
      } else {
        setSearchParams(stringifyQueryParams({ ...existingParams }))
      }
    },

    setColumnOrdering: (column: CallTableColumn, direction: SortDirection) => {
      let orderingField: OrderingField<Call> | undefined
      switch (column) {
        case CallTableColumn.DateTime:
          if (direction === SortDirection.Ascending) {
            orderingField = 'started_at'
          } else {
            orderingField = '-started_at'
          }
          break
        case CallTableColumn.Duration:
          if (direction === SortDirection.Ascending) {
            orderingField = 'duration_in_seconds'
          } else {
            orderingField = '-duration_in_seconds'
          }
          break
      }

      const existingParams = Object.fromEntries(searchParams)
      delete existingParams.sb
      setSearchParams(
        stringifyQueryParams({ ...existingParams, sb: orderingField }),
      )
    },
    setSelectedFilterData: (selectedFilters: SelectedFilterData[]) => {
      const selectedFilterParams = tryGetQueryFilters(t, selectedFilters)
      const routeQueryParams: CallsRouteQueryParameters = {
        dir: selectedFilterParams.directions,
        dur_gte: selectedFilterParams.duration_in_seconds_gte,
        dur_lt: selectedFilterParams.duration_in_seconds_lt,
        status: selectedFilterParams.statuses,
        vmr: getBitValueOrDefault(selectedFilterParams.has_voicemail),
        miss: getBitValueOrDefault(selectedFilterParams.is_missed),
        trnsfr: getBitValueOrDefault(
          selectedFilterParams.is_externally_transferred,
        ),
        u: selectedFilterParams.related_user_ids,
        agent_sentiment: selectedFilterParams.office_overall_sentiment_score,
        customer_sentiment:
          selectedFilterParams.customer_overall_sentiment_score,
        vp: selectedFilterParams.voip_profile_ids,
      }

      const existingParams = Object.fromEntries(searchParams)
      const selectedFilterKeys = Object.keys(selectedFilterParams)
      const unselectedFilterKeys = callFilterKeys.filter(
        (key) => !selectedFilterKeys.includes(key),
      )

      for (const key of unselectedFilterKeys) {
        const routeParamKey = stateParamToRouteParam[key]
        delete existingParams[routeParamKey]
        delete routeQueryParams[routeParamKey]
      }
      setSearchParams(
        stringifyQueryParams({ ...existingParams, ...routeQueryParams }),
      )
    },
    setAudioTimestamp: (timestamp?: number) => {
      appendState({ audioTimestamp: timestamp })
    },
    setDateTimeRange: (dateRange: DateRange) => {
      const existingParams = Object.fromEntries(searchParams)
      delete existingParams.date_range
      delete existingParams.aftr
      delete existingParams.bfr

      let dateRangeIdentifier: DateRangeIdentifier | undefined
      const routeQueryParams: CallsRouteQueryParameters = {}
      if (DateRange.areEqual(dateRange, DateRange.today())) {
        dateRangeIdentifier = DateRangeIdentifier.Today
      } else if (DateRange.areEqual(dateRange, DateRange.yesterday())) {
        dateRangeIdentifier = DateRangeIdentifier.Yesterday
      } else if (DateRange.areEqual(dateRange, DateRange.last7Days())) {
        dateRangeIdentifier = DateRangeIdentifier.Last7Days
      } else if (DateRange.areEqual(dateRange, DateRange.last14Days())) {
        dateRangeIdentifier = DateRangeIdentifier.Last14Days
      } else if (DateRange.areEqual(dateRange, DateRange.last30Days())) {
        dateRangeIdentifier = DateRangeIdentifier.Last30Days
      } else if (hasValue(dateRange.after) && hasValue(dateRange.before)) {
        routeQueryParams.aftr = dateRange.after?.getTime()
        routeQueryParams.bfr = dateRange.before?.getTime()
      }

      if (dateRangeIdentifier) {
        routeQueryParams.date_range = dateRangeIdentifier
      }

      setSearchParams(
        stringifyQueryParams({ ...existingParams, ...routeQueryParams }),
      )
    },
    navigateToCallDetails: (
      callId: number,
      tab: TCallAnalyticsTab = 'overview',
    ) => {
      if (tab) {
        appendState({ selectedCallInitialTab: tab })
      }
      searchParams.delete('t')
      navigate(
        `${ApplicationRoutes.CALLS}/${callId}?${searchParams.toString()}`,
      )
    },
    appendState: (newState: Partial<CallsViewModelState>) => {
      appendState(newState)
    },
  }

  useEffect(() => {
    appendState({
      searchValue: {
        value: stateFromQueryParams.search ?? '',
        label: stateFromQueryParams.search ?? '',
      },
    })
  }, [stateFromQueryParams.search])

  return {
    state: {
      ...__lifecycleState,
      ...stateFromQueryParams,
      dateTimeRange: dateTimeRange,
      isCallsLoading: isCallsLoading || isFetchingNextPage,
      callsListState: {
        data: results,
        hasNextPage: !!hasNextPage,
        nextPageOffset: sourceState.callsListState?.nextPageOffset ?? 0,
      },
      totalFilteredCallCount: totalCount,
    } as CallsViewModelState,
    actions: { ...actions },
  }
}

// Define the shape of the context
interface CallsViewModelContextType {
  state: CallsViewModelState
  actions: {
    tryGetNextCallPage: () => void
    setSearchValue: (searchValue?: SearchValue) => void
    setColumnOrdering: (
      column: CallTableColumn,
      direction: SortDirection,
    ) => void
    setSelectedFilterData: (selectedFilters: SelectedFilterData[]) => void
    setAudioTimestamp: (timestamp?: number) => void
    setDateTimeRange: (dateRange: DateRange) => void
    navigateToCallDetails: (callId: number, tab?: TCallAnalyticsTab) => void
    appendState: (newState: Partial<CallsViewModelState>) => void
  }
}

// Create the context with a default empty value
const CallsViewModelContext = createContext<
  CallsViewModelContextType | undefined
>(undefined)

// Props for the provider component
interface CallsViewModelProviderProps {
  children: ReactNode
  sharedStateKey?: string
  deleteSharedOnUnmount?: boolean
}

// Provider component that wraps the useCallsViewModel hook
export const CallsViewModelProvider: React.FC<CallsViewModelProviderProps> = ({
  children,
  sharedStateKey = '__default',
  deleteSharedOnUnmount = false,
}) => {
  const viewModel = useCallsViewModel({
    sharedStateKey,
    deleteSharedOnUnmount,
  })

  return (
    <CallsViewModelContext.Provider value={viewModel}>
      {children}
    </CallsViewModelContext.Provider>
  )
}

// Custom hook to use the CallsViewModel context
export const useCallsViewModelContext = (): CallsViewModelContextType => {
  const context = useContext(CallsViewModelContext)
  if (context === undefined) {
    throw new Error(
      'useCallsViewModelContext must be used within a CallsViewModelProvider',
    )
  }
  return context
}
